import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LifeBuoy,
  UserCog,
  Coins,
  Radio,
  Music4,
  ShieldAlert,
  Lock,
  Mail,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT_EMAIL } from "@/constants/site";
import { SmartStoreBadges, StoreQRCodes } from "@/components/StoreBadges";

const topics = [
  {
    icon: UserCog,
    t: "Account & sign-in",
    items: [
      { q: "How do I create an account?", a: "Download AuraLive and sign in with Google, or with Apple on iOS. Your viewer and creator profile is created automatically the first time you sign in." },
      { q: "Can I use one profile across Apple and Google?", a: "Account linking (a single profile across Apple + Google sign-in) is on our roadmap. For now, please sign in with the same provider you first used so your coins, follows and history stay together." },
      { q: "How do I delete my account?", a: "Email us at " + CONTACT_EMAIL + " from your account email and we'll process the deletion in line with our Privacy Policy." },
    ],
  },
  {
    icon: Coins,
    t: "Coins & payments",
    items: [
      { q: "How do I buy coins?", a: "Buy coins through the App Store, Google Play, or on the web. New creators also get 500 welcome coins to start." },
      { q: "Can I get a refund?", a: "Coin purchases are handled by Apple and Google, so refunds are requested through them (reportaproblem.apple.com or Google Play order history). See our Coins & Refund Policy for details." },
      { q: "How do creators cash out?", a: "Creators earn coins from gifts and tips and can cash out once they meet the minimum threshold and complete identity verification. Cash-outs may take up to 30 days for review." },
    ],
  },
  {
    icon: Radio,
    t: "Going live",
    items: [
      { q: "How do I go live?", a: "Tap the camera button, add a title, choose your audience, and broadcast. Creators verify their identity before their first live stream." },
      { q: "Why do I need to verify my identity?", a: "Identity verification (via Stripe Identity) keeps AuraLive authentic and accountable, and is required before you can broadcast." },
      { q: "My stream keeps dropping — what can I do?", a: "Check your connection (a stable Wi-Fi or strong mobile signal is best), close background apps, and make sure the app has camera and microphone permissions. If it persists, email us with your device model and the time it happened." },
    ],
  },
  {
    icon: Music4,
    t: "AI Music Studio",
    items: [
      { q: "How do I make a song?", a: "Open the Music Lab, name your track, pick from 10 languages and 12 genres, describe the vibe, write your own lyrics or tap “Write with AI”, generate cover art, then create — your track composes in about a minute." },
      { q: "What does it cost?", a: "You get 2 free songs a day (30s each). Beyond that it's 60 coins per 30-second block, with selectable lengths up to 120s. Coins are refunded automatically if a generation fails." },
      { q: "Can others use my track?", a: "Yes — publish it as a sound and others can tap “use this sound” to go live or post with it. Popular tracks climb the city-first Trending Sounds chart." },
    ],
  },
  {
    icon: ShieldAlert,
    t: "Safety & reporting",
    items: [
      { q: "How do I report a stream or user?", a: "Use the in-app report and block tools during any stream or on a profile, or email " + CONTACT_EMAIL + " with details. Our team reviews reports promptly." },
      { q: "What content is not allowed?", a: "Illegal content, harassment, hate speech, and explicit content are never allowed. See our Community Guidelines for the full list." },
    ],
  },
  {
    icon: Lock,
    t: "Privacy & data",
    items: [
      { q: "Is my location shared?", a: "Your precise location is only used while you're actively broadcasting. When you go offline, only a coarse, city-level area may remain for discovery. You can disable location at the OS level anytime." },
      { q: "How do I access or delete my data?", a: "Email " + CONTACT_EMAIL + " to request access to, correction of, or deletion of your personal data. See our Privacy Policy for your full rights." },
    ],
  },
];

export default function Support() {
  return (
    <div data-testid="support-page">
      {/* Hero */}
      <section className="px-6 pb-10 pt-32 md:px-12 md:pt-44">
        <div className="mx-auto max-w-4xl text-center">
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-aura-pink"
          >
            <LifeBuoy size={14} /> Help & Support
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-6 font-heading text-4xl font-semibold tracking-tight text-aura-ink sm:text-5xl md:text-6xl"
          >
            How can we <span className="grad-text">help?</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-aura-text2"
          >
            Find quick answers about your account, coins, going live and the AI
            Music Studio. Can't find it? We're one email away.
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="support-email-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-aura-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-aura-ink2"
          >
            <Mail size={17} /> {CONTACT_EMAIL}
          </motion.a>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-10 flex max-w-xl flex-col items-center gap-6 rounded-3xl border border-black/[0.06] bg-white p-6 solid-card sm:flex-row sm:justify-between sm:text-left"
            data-testid="support-get-app"
          >
            <div>
              <p className="font-heading text-lg font-semibold text-aura-ink">Get AuraLive</p>
              <p className="mt-1 text-sm text-aura-text2">Out now on iPhone &amp; Android.</p>
              <SmartStoreBadges idPrefix="support" className="mt-4" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <StoreQRCodes testIdPrefix="support" size={84} />
              <span className="text-xs font-medium text-aura-text2">Scan to download</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Topics */}
      <section className="px-6 py-12 md:px-12">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
          {topics.map((topic, ti) => (
            <motion.div
              key={topic.t}
              data-testid={`support-topic-${ti}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (ti % 2) * 0.06 }}
              className="rounded-3xl border border-black/[0.06] bg-white p-7 solid-card md:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl grad-bg text-white">
                  <topic.icon size={20} />
                </span>
                <h2 className="font-heading text-xl font-medium tracking-tight text-aura-ink">
                  {topic.t}
                </h2>
              </div>
              <Accordion type="single" collapsible className="mt-4 w-full">
                {topic.items.map((it, i) => (
                  <AccordionItem key={i} value={`t${ti}-i${i}`} className="border-black/10">
                    <AccordionTrigger className="py-4 text-left text-sm font-semibold text-aura-ink hover:text-aura-pink hover:no-underline">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-6 text-aura-text2">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still need help */}
      <section className="px-6 py-16 md:px-12">
        <div className="mx-auto max-w-6xl rounded-[2rem] border border-black/[0.06] bg-white p-10 solid-card md:p-14">
          <div className="grid items-center gap-8 md:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-aura-ink md:text-4xl">
                Still need a hand?
              </h2>
              <p className="mt-4 max-w-lg leading-relaxed text-aura-text2">
                Reach our team directly and we'll get back to you as soon as we
                can. For urgent safety issues, please also use the in-app report
                and block tools.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="support-contact-link"
                  className="inline-flex items-center gap-2 rounded-full bg-aura-ink px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-aura-ink2"
                >
                  Contact us <ArrowRight size={16} />
                </Link>
                <Link
                  to="/safety"
                  className="inline-flex items-center gap-2 rounded-full border border-black/15 px-6 py-3 text-sm font-semibold text-aura-ink transition-colors hover:border-black/40"
                >
                  Community Guidelines
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-black/[0.06] bg-aura-base p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-muted">
                Helpful links
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  { to: "/terms", label: "Terms of Service" },
                  { to: "/privacy", label: "Privacy Policy" },
                  { to: "/refund", label: "Coins & Refund Policy" },
                  { to: "/safety#security", label: "Security" },
                ].map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="inline-flex items-center gap-2 font-medium text-aura-ink transition-colors hover:text-aura-pink">
                      <ArrowRight size={14} className="text-aura-pink" /> {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
